import {
  useBuyerPrequalificationAttempts,
  useSetPackageLetterOverride,
} from "@/hooks/buyers/usePrequalificationInvites";
import { Skeleton, useToast } from "@/design-system/components/ui";
import { qualifiedAmountLabel } from "./prequalification/qualifiedAmount";
import { NOT_TIED_TO_A_LISTING } from "./prequalification/AttemptsSection";
import { resolvedShortListingName } from "@/lib/listingDisplay";

/**
 * Which prequalification letter THIS submission's package uses (PR 3b).
 *
 * <p>The package defaults to the buyer's most recent qualified letter. A
 * specialist may override it per submission — and the override survives a
 * regenerate, because it is stored on the submission rather than beside
 * <c>PackageDocumentId</c>, which regeneration replaces.</p>
 *
 * <p>🔴 <b>Every option names the LISTING it was sized against.</b> That is what
 * makes the simple default safe to override: a letter sized for a $2M business
 * attached to a $600K submission is the failure this exists to prevent, and a
 * picker showing bare reference numbers could not warn about it. The binding
 * comes from the attempt's listing pointer — the reason the attempts collection
 * is a Buyer-module table at all.</p>
 *
 * <p>Setting the pointer does NOT regenerate. The specialist chooses when to
 * spend a render, and an automatic one would silently replace a package that may
 * already have been sent.</p>
 */
export function PackageLetterOverrideField({
  buyerLeadId,
  submissionId,
  value,
  canEdit,
}: {
  buyerLeadId: string;
  submissionId: string;
  value?: string | null;
  canEdit: boolean;
}) {
  const toast = useToast();
  const attempts = useBuyerPrequalificationAttempts(buyerLeadId);
  const save = useSetPackageLetterOverride(submissionId);

  if (attempts.isLoading) return <Skeleton className="h-9 w-full" />;

  // Only an attempt that actually carries a letter can back a package. Offering
  // one that cannot is offering a choice that resolves to "no letter".
  const options = (attempts.data ?? []).filter((a) => a.hasLetter && !a.unavailable);
  const primary = options.find((a) => a.isPrimary);

  if (options.length === 0) {
    return (
      <span className="text-caption text-gray-mid">
        No prequalification letter is on file for this buyer yet.
      </span>
    );
  }

  if (!canEdit) {
    const chosen = options.find((a) => a.submissionId === value);
    return (
      <span className="text-small text-ink">
        {chosen ? describe(chosen) : `Default${primary ? ` — ${describe(primary)}` : ""}`}
      </span>
    );
  }

  return (
    <select
      data-testid="package-letter-override"
      aria-label="Prequalification letter for this package"
      className="w-full rounded-sm border border-gray-mid bg-white-pure px-space-2 py-[6px] text-small text-ink"
      value={value ?? ""}
      disabled={save.isPending}
      onChange={(e) => {
        // "" is the DEFAULT, which clears the override — a nullable field where
        // no value means default is the whole contract, so there is no separate
        // "clear" action to get out of step with it.
        const next = e.target.value || null;
        save.mutate(next, {
          onSuccess: () =>
            toast.success(next ? "Letter override set." : "Back to the default letter."),
          onError: (err: Error) => toast.error(err.message),
        });
      }}
    >
      <option value="">
        {primary ? `Default — ${describe(primary)}` : "Default (most recent qualified letter)"}
      </option>
      {options.map((a) => (
        <option key={a.id} value={a.submissionId}>
          {describe(a)}
        </option>
      ))}
    </select>
  );
}

/**
 * Reference, then what it was SIZED AGAINST, then the amount — the three facts
 * a specialist needs to notice a mismatch before sending.
 *
 * <p>🔴 The listing name comes through the ONE ladder, SHORT. This is an
 * <c>&lt;option&gt;</c>, which cannot wrap and cannot carry a tooltip, so a
 * marketplace title with its pitch attached would push the qualified amount —
 * the figure the mismatch is spotted from — off the end of the row. Reading the
 * raw business-name column was worse still: a listing whose seller has not named
 * the business rendered as unlisted, which is a claim about the attempt rather
 * than about the listing's naming.</p>
 *
 * <p>🔴 The genuinely-unlisted case says {@link NOT_TIED_TO_A_LISTING} — the
 * SAME words the attempts list uses. It said "no listing" here and "Not tied to
 * a listing" there, so one attempt read as two different facts depending on
 * which surface you met it on. Prose rather than an em dash by Table and Tile
 * Standard §6 "state absent"; see the constant for why this is not §7's plain
 * missing value.</p>
 */
function describe(a: {
  referenceNumber?: string | null;
  listingBusinessName?: string | null;
  listingDisplayName?: string | null;
  qualifiedAmount?: number | null;
  hasQualifiedAmount?: boolean;
}): string {
  const parts = [a.referenceNumber ?? "Prequalification"];
  const name = resolvedShortListingName(a.listingDisplayName, a.listingBusinessName);
  parts.push(name ? `for ${name}` : NOT_TIED_TO_A_LISTING);
  const sizing = qualifiedAmountLabel(a);
  if (sizing) parts.push(sizing);
  return parts.join(" · ");
}
